import React, { Fragment, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

import { EndPoints } from "../../Helpers/Constants/EndPoints";
import Loader from "../Common/Loader";
import Switch from "react-switch";
import API from "../../Helpers/Constants/EndPoints/Api";
import Axios from "../../Helpers/Universal/Axios/Axios";
import {
  BLOCK_STATUS,
  LIMIT,
  NA,
  OFFSET,
  ADMIN_TYPE
} from "../../Helpers/Constants/Default";
import Paginate from "../Common/Paginate";
import DatePicker from "../Common/DatePicker";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import DataNotFound from "../Common/DataNotFound";
import { City, State } from "country-state-city";

const Managers = ({ permissions: { all, add, edit, delete: del } }) => {
  const paginate = useSelector((val) => val.paginate);
  const accType = useSelector((val) => val.accType.accType);
  const profileData = useSelector((val) => val.profile);
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState({
    loader: false,
    deleteConformation: { data: false, visible: false },
  });
  
  // Separate states for each filter
  const [adminFilter, setAdminFilter] = useState([]);
  const [subAdminFilter, setSubAdminFilter] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  
  const [filter, setFilter] = useState({
    limit: LIMIT,
    offset: paginate?.manager || OFFSET,
  });

  // Create refs for dropdowns
  const dropdownRefs = useRef({});

  const getData = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build query parameters from individual filter states
      const params = {
        limit: filter.limit,
        offset: filter.offset
      };
      
      // Add filters only if they have values
      if (adminFilter.length > 0) {
        params.adminId = adminFilter.map(a => a.value);
      }
      
      if (subAdminFilter.length > 0) {
        params.subAdminId = subAdminFilter.map(s => s.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.MANAGER.LISTING, params);
      if (status) setData(data);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleBlockAccess = async ({ _id, blockAccess }) => {
    try {
      const { status } = await Axios.patch(API.MANAGER.UPDATE, {
        managerId: _id,
        blockAccess: !blockAccess,
      });
      if (status)
        setData((prev) => ({
          ...prev,
          data: prev?.data?.map((val) =>
            val._id === _id ? { ...val, blockAccess: !blockAccess } : val
          ),
        }));
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleResetPassword = async (managerId) => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      await Axios.get(API.MANAGER.RESET_PASSWORD + "/" + managerId);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const exportExcel = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build params for export the same way as getData
      const params = {};
      
      if (adminFilter.length > 0) {
        params.adminId = adminFilter.map(a => a.value);
      }
      
      if (subAdminFilter.length > 0) {
        params.subAdminId = subAdminFilter.map(s => s.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.REPORT.MANAGER, params);

      if (status) window.open(data, "_blank");
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleAction = (managerId, e) => {
    e.stopPropagation();
    setData((prev) => ({
      ...prev,
      data: prev?.data?.map((val) =>
        val._id === managerId && !val?.action
          ? { ...val, action: true }
          : { ...val, action: false }
      ),
    }));
  };

  // Close all dropdowns when clicking outside
  const handleClickOutside = (e) => {
    // Check if click is outside any dropdown
    const isOutside = Object.values(dropdownRefs.current).every(ref => {
      return ref && !ref.contains(e.target);
    });
    
    if (isOutside) {
      setData((prev) => ({
        ...prev,
        data: prev?.data?.map((val) => ({
          ...val,
          action: false,
        })),
      }));
    }
  };

  useEffect(() => {
    // Add event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);
    
    // Remove event listener when component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBlockUnblock = async ({ _id, isBlocked }) => {
    try {
      Swal.fire({
        title: `${isBlocked ? "Block" : "Un-block"} Manager`,
        text: `Are you sure you want to ${isBlocked ? "Block" : "Un-block"}?`,
        confirmButtonText: "Yes",
        showCancelButton: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { status } = await Axios.patch(API.MANAGER.UPDATE, {
            managerId: _id,
            isBlocked: !isBlocked,
          });
          if (status)
            setData((prev) => ({
              ...prev,
              data: prev?.data?.map((val) =>
                val._id === _id ? { ...val, isBlocked: !isBlocked } : val
              ),
            }));
        }
      });
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const [states, setStatesAndUTs] = useState([]);
  const [cities, setCities] = useState([]);
  
  useEffect(() => {
    setStatesAndUTs(State.getStatesOfCountry("IN"));
  }, []);

  const handleStateChange = (selectedOption) => {
    if (!selectedOption) {
      setStateFilter(null);
      setCities([]);
      setCityFilter(null);
      return;
    }

    const stateCode = selectedOption.label;
    setStateFilter(stateCode);
    
    const stateCities = City.getCitiesOfState("IN", selectedOption.value);
    setCities(stateCities);
    setCityFilter(null); // Reset city filter when state changes
  };

  const handleCityChange = (selectedOption) => {
    if (!selectedOption) {
      setCityFilter(null);
      return;
    }
    
    setCityFilter(selectedOption.value);
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const handleAdminChange = (selectedOptions) => {
    setAdminFilter(selectedOptions || []);
  };

  const handleSubAdminChange = (selectedOptions) => {
    setSubAdminFilter(selectedOptions || []);
  };

  const [dropdown, setDropdown] = useState({ 
    admins: [], 
    subAdmins: [],
    adminOptions: [],
    subAdminOptions: []
  });

  const getDropdowns = async () => {
    try {
      let subAdminData = [];
      let adminData = [];
      
      if (accType == ADMIN_TYPE.SUPER_ADMIN) {
        adminData = await Axios.get(API.DROPDOWN.ADMIN, { accountType: ADMIN_TYPE.ADMIN });
        subAdminData = await Axios.get(API.DROPDOWN.ADMIN, { accountType: ADMIN_TYPE.SUB_ADMIN });
        
        if (subAdminData?.status) {
          const subAdminOptions = subAdminData.data.map(admin => ({
            value: admin._id,
            label: admin.name
          }));
          setDropdown((prev) => ({ ...prev, subAdmins: subAdminData.data, subAdminOptions }));
        }
        
        if (adminData?.status) {
          const adminOptions = adminData.data.map(admin => ({
            value: admin._id,
            label: admin.name
          }));
          setDropdown((prev) => ({ ...prev, admins: adminData.data, adminOptions }));
        }
      } else if (accType == ADMIN_TYPE.ADMIN) {
        subAdminData = await Axios.get(API.DROPDOWN.ADMIN, { accountType: ADMIN_TYPE.SUB_ADMIN });
        
        if (subAdminData?.status) {
          const subAdminOptions = subAdminData.data.map(admin => ({
            value: admin._id,
            label: admin.name
          }));
          const adminOptions = [{
            value: profileData.id,
            label: profileData.name
          }];
          
          setDropdown((prev) => ({ 
            ...prev, 
            subAdmins: subAdminData.data, 
            admins: [{ _id: profileData.id, name: profileData.name }],
            subAdminOptions,
            adminOptions
          }));
        }
      } else {
        const subAdminOptions = [{
          value: profileData.id,
          label: profileData.name
        }];
        
        setDropdown((prev) => ({ 
          ...prev, 
          subAdmins: [{ _id: profileData.id, name: profileData.name }],
          subAdminOptions 
        }));
      }
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  // Auto-refresh data when any filter changes
  useEffect(() => {
    // Reset to first page when filters change
    setFilter(prev => ({...prev, offset: 0}));
  }, [adminFilter, subAdminFilter, searchFilter, stateFilter, cityFilter]);

  // Get data when filter changes (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getData();
    }, searchFilter ? 500 : 0); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [filter.offset, filter.limit, adminFilter, subAdminFilter, searchFilter, stateFilter, cityFilter]);

  useEffect(() => {
    getDropdowns();
  }, []);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      backgroundColor: "#2d3748",
      borderColor: state.isFocused ? "#63b3ed" : "#4a5568",
      boxShadow: state.isFocused ? "0 0 0 1px #63b3ed" : provided.boxShadow,
      color: "white",
      "&:hover": {
        borderColor: "#63b3ed"
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#a0aec0",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#4a5568"
        : state.isFocused
        ? "#374151"
        : "#2d3748",
      color: "white",
      "&:active": {
        backgroundColor: "#4a5568"
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#4a5568",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      ":hover": {
        backgroundColor: "#e53e3e",
        color: "white",
      },
    }),
    input: (provided) => ({
      ...provided,
      color: "white",
    })
  };

  // Create options for states and cities
  const stateOptions = states.map(state => ({
    value: state.isoCode,
    label: state.name
  }));

  const cityOptions = cities.map(city => ({
    value: city.name,
    label: city.name
  }));

  return (
    <Fragment>
      {visible.loader && <Loader />}
      <div className="container-fluid py-3">
        <div className="dateselect_navigation d-flex justify-content-between align-items-center">
          <div className="d-flex main_bar">
            {/* Conditional rendering based on account type */}
            {(accType === ADMIN_TYPE.SUPER_ADMIN || accType === ADMIN_TYPE.ADMIN) && (
              <>
                {/* Admin Select - Multi Select */}
                <div className="mx-2" style={{ minWidth: "200px" }}>
                  <Select
                    value={adminFilter}
                    options={dropdown.adminOptions}
                    onChange={handleAdminChange}
                    placeholder="Select Admin"
                    isClearable
                    isMulti
                    styles={customStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Sub Admin Select - Multi Select */}
                <div className="mx-2" style={{ minWidth: "200px" }}>
                  <Select
                    value={subAdminFilter}
                    options={dropdown.subAdminOptions}
                    onChange={handleSubAdminChange}
                    placeholder="Select Sub Admin"
                    isClearable
                    isMulti
                    styles={customStyles}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </>
            )}

            {accType === ADMIN_TYPE.SUB_ADMIN && (
              /* Sub Admin Select - Multi Select (only for sub-admin) */
              <div className="mx-2" style={{ minWidth: "200px" }}>
                <Select
                  value={subAdminFilter}
                  options={dropdown.subAdminOptions}
                  onChange={handleSubAdminChange}
                  placeholder="Select Sub Admin"
                  isClearable
                  isMulti
                  styles={customStyles}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            )}

            {/* Search Box */}
            <div className="d-flex position-relative bg-gradient-dark rounded mx-2" style={{ height: "42px" }}>
              <i
                className="fa fa-search position-absolute top-50 translate-middle-y ms-3 text-white"
                style={{ zIndex: 10 }}
                aria-hidden="true"
              ></i>
              <input
                value={searchFilter}
                onChange={handleSearchChange}
                type="search"
                className="text-start form-control bg-dark text-white border-0 ps-5 h-100"
                style={{ 
                  cursor: "unset", 
                  width: "100px",
                  position: "relative"
                }}
                name="gsearch"
                placeholder="Search..."
              />
            </div>
            
            {/* State Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={stateOptions.find(opt => opt.label === stateFilter) || null}
                options={stateOptions}
                onChange={handleStateChange}
                placeholder="Select State"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            
            {/* City Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={cityOptions.find(opt => opt.value === cityFilter) || null}
                options={cityOptions}
                onChange={handleCityChange}
                placeholder="Select City"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={!stateFilter}
              />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2 mob-margin-none">
              {(all || add) && (
                <Link to={EndPoints.ADD_MANAGER}>
                  <button className="btn bg-gradient-dark float-end d-flex btn-md mb-1 align-items-center h-42">
                    <i className="fa fa-plus me-2 mt-1" aria-hidden="true"></i>
                    Create
                  </button>
                </Link>
              )}
            </div>
            <button
              className="btn bg-gradient-dark float-end d-flex btn-md mb-1 align-items-center h-42 data-del"
              onClick={exportExcel}
            >
              <i className="fa fa-download me-2 mt-1" aria-hidden="true"></i>
              Export
            </button>
          </div>
        </div>
        {data?.data?.length === 0 ? (
          <DataNotFound />
        ) : (
          <Fragment>
            <div className="overflow-table">
              <table className="page-table">
                <thead>
                  <tr>
                    <th scope="col" className="text-uppercase">
                      #
                    </th>
                    <th scope="col" className="text-uppercase">
                      User Id
                    </th>
                    <th scope="col" className="text-uppercase">
                      Name
                    </th>
                    <th scope="col" className="text-uppercase">
                      Supervisors
                    </th>
                    <th scope="col" className="text-uppercase">
                      Agent
                    </th>
                    <th scope="col" className="text-uppercase">
                      City
                    </th>
                     <th scope="col" className="text-uppercase">
                      State
                    </th>
                    {(all || edit || del) && (
                      <th scope="col" className="text-uppercase">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((val, i) => (
                    <tr key={val._id}>
                      <td>{++i + filter.offset}</td>
                      <td>
                        <Link to={EndPoints.MANAGER} state={val._id}>
                          {val.userName}
                        </Link>
                      </td>
                      <td>{val.name}</td>
                      <td>{val.supervisorCount}</td>
                      <td>{val.agentCount}</td>
                      <td>{val?.address?.city?.toUpperCase()}</td>
                      <td>{val?.address?.state?.toUpperCase()}</td>
                      {(all || edit || del) && (
                        <td>
                          <div
                            className="position-relative"
                            ref={el => dropdownRefs.current[val._id] = el}
                          >
                            <i
                              className="material-icons ps-2 cursior-pointer"
                              onClick={(e) => handleAction(val._id, e)}
                            >
                              more_horiz
                            </i>
                            <div className="more-ic">
                              <ul
                                className="icon-list"
                                style={{
                                  display: val?.action ? "block" : "none",
                                }}
                              >
                                <li onClick={() => handleBlockUnblock(val)}>
                                  {val.isBlocked
                                    ? BLOCK_STATUS.UN_BLOCKED
                                    : BLOCK_STATUS.BLOCKED}
                                </li>
                                <li>
                                  <Link
                                    to={EndPoints.EDIT_MANAGER}
                                    state={{
                                      ...val,
                                      password: val.dcryptedPass,
                                    }}
                                  >
                                    Edit
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginate
              count={data?.count}
              filter={filter}
              setFilter={setFilter}
              sliceKey={"manager"}
              forcePage={paginate?.manager / 10}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Managers;